/**
 * DF-I3 (timezone-display half) — telling the client WHOSE clock the slot is on.
 *
 * The meeting date is entered in the SHOOT's timezone, which is often not the
 * client's: a Zurich agency booking a Berlin shoot, an owner abroad. Legacy said
 * so twice — a warning above the fields whenever the two differ, and, once a
 * slot was picked, an echo of it in both zones
 * (`MeetingInstructionsFields.component.tsx`). v2 rendered neither, so "09:00"
 * was ambiguous with nothing on screen to disambiguate it.
 *
 * This was unbuildable until 2026-08-24: the BE returned `timezone: null` on
 * every cart because the FE sent `(0,0)` for the property. With real
 * coordinates the shoot timezone resolves, so both halves have their input.
 *
 * Kept pure so the rules are testable without mounting the step or freezing the
 * clock — `isSameTimezone` compares the CURRENT offsets, which is what makes
 * `Europe/Berlin` and `Europe/Prague` the same thing to a client and different
 * strings to a computer.
 */

import moment from 'moment-timezone'

import { isSameTimezone } from 'utils/time'

export interface TimezoneComparison {
  /** Render the warning + the dual echo? False whenever they agree, or whenever
   *  the shoot timezone is unknown — an unknown one is not a mismatch. */
  differs: boolean
  /** The shoot-location timezone, when resolved. */
  locationTimezone?: string
  /** The client's own timezone. */
  userTimezone: string
}

/**
 * Do the client's and the shoot's timezones differ enough to say so?
 *
 * Compares OFFSETS, not names, exactly as legacy did: two zones that are
 * nominally different but currently identical (Berlin / Prague) would otherwise
 * produce a warning that reads as a mistake.
 *
 * @example
 * compareTimezones('Europe/Zurich', 'Europe/Berlin')     // differs: false
 * compareTimezones('Europe/Zurich', 'America/New_York')  // differs: true
 * compareTimezones('Europe/Zurich', undefined)           // differs: false
 */
export const compareTimezones = (
  userTimezone: string,
  locationTimezone?: string
): TimezoneComparison => {
  if (!locationTimezone || !userTimezone) {
    return { differs: false, locationTimezone, userTimezone }
  }

  return {
    differs: !isSameTimezone(userTimezone, locationTimezone),
    locationTimezone,
    userTimezone,
  }
}

/** One line of the dual echo: the same instant, read in one timezone. */
export interface TimezoneReading {
  timezone: string
  /** Formatted date + time in that zone. */
  label: string
}

/**
 * The chosen slot read in both timezones, shoot first.
 *
 * Shoot first is deliberate: it is the zone the field is entered in, so it is
 * the one the client must recognise as what they typed. The second line is the
 * translation into their own clock.
 *
 * Returns `[]` when there is nothing to disambiguate — no date, no resolved
 * shoot timezone, or the two zones agree — so the caller renders nothing rather
 * than an echo that repeats itself.
 *
 * @example
 * dualTimezoneReadings('2026-09-03T08:30:00Z', 'Europe/Berlin', 'America/New_York')
 * // [{ timezone: 'Europe/Berlin', label: '03. 09. 2026 10:30' },
 * //  { timezone: 'America/New_York', label: '03. 09. 2026 04:30' }]
 */
export const dualTimezoneReadings = (
  isoDate: string | undefined,
  locationTimezone: string | undefined,
  userTimezone: string
): TimezoneReading[] => {
  if (!isoDate || !locationTimezone || !userTimezone) return []
  if (isSameTimezone(userTimezone, locationTimezone)) return []

  const at = moment(isoDate)
  if (!at.isValid()) return []

  return [
    { timezone: locationTimezone, label: at.clone().tz(locationTimezone).format('DD. MM. YYYY HH:mm') },
    { timezone: userTimezone, label: at.clone().tz(userTimezone).format('DD. MM. YYYY HH:mm') },
  ]
}
